import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  ArrowLeftIcon,
  CpuChipIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  DocumentCheckIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const CreateAgent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [creatingWhatsApp, setCreatingWhatsApp] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [instructionsManuallyEdited, setInstructionsManuallyEdited] = useState(false);
  const [maxSystemPromptTokens, setMaxSystemPromptTokens] = useState(2000);

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);

  // Website training states
  const [websiteTrainingEnabled, setWebsiteTrainingEnabled] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteVerificationStatus, setWebsiteVerificationStatus] = useState(null);
  const [websiteVerificationError, setWebsiteVerificationError] = useState('');

  // FAQ training states
  const [faqTrainingEnabled, setFaqTrainingEnabled] = useState(false);
  const [faqPairs, setFaqPairs] = useState([{ question: '', answer: '', id: Date.now() }]);

  // Async task tracking states
  const [isAsyncMode, setIsAsyncMode] = useState(
    process.env.REACT_APP_ASYNC_MODE === 'true' || true  // Default to true for now
  );
  const [asyncTaskId, setAsyncTaskId] = useState(null);
  const [taskProgress, setTaskProgress] = useState([]);
  const [taskStatus, setTaskStatus] = useState(null);
  const [estimatedCompletion, setEstimatedCompletion] = useState(null);
  const [socketConnection, setSocketConnection] = useState(null);
  const socketRef = useRef(null);
  const [hasShownValidationErrors, setHasShownValidationErrors] = useState(false);

  // Document limits state
   const [documentLimits, setDocumentLimits] = useState({
     maxDocumentSizeBytes: 4194304, // 4MB default (scale package)
     maxTextLength: 4000, // 4K default (scale package)
     maxTokensPerDocument: 1000 // 1K default (scale package)
   });
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [packageLimits, setPackageLimits] = useState(null);

  // Tools state
  const [availableTools, setAvailableTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(true);

  // FAQ helper functions
  const addFaqPair = () => {
    setFaqPairs(prev => [...prev, { question: '', answer: '', id: Date.now() }]);
  };

  const removeFaqPair = (id) => {
    setFaqPairs(prev => prev.filter(pair => pair.id !== id));
  };

  const updateFaqPair = (id, field, value) => {
    setFaqPairs(prev => prev.map(pair =>
      pair.id === id ? { ...pair, [field]: value } : pair
    ));
  };

  const [formData, setFormData] = useState({
    name: '',
    instructions: 'You are Snwolley, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.',
    model: 'snwolley-2.0-alpha',
    temperature: 0.7,
    max_tokens: 1500,
    knowledge_base_enabled: false,
    auto_reply_enabled: true,
    response_delay_ms: 1000,
    typing_simulation: true,
    website_url: ''
  });

  // Fetch system settings
  const fetchSystemSettings = async () => {
    try {
      const authToken = localStorage.getItem('snwolley_auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/system/settings`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings.max_system_prompt_tokens) {
          setMaxSystemPromptTokens(data.settings.max_system_prompt_tokens);
        }
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  // Fetch available tools
  const fetchAvailableTools = async () => {
    try {
      setLoadingTools(true);
      const response = await api.getTools();
      setAvailableTools(response.data.tools || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error('Failed to load available tools');
    } finally {
      setLoadingTools(false);
    }
  };

  // Check verification status and fetch document limits on component mount
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const response = await api.getVerificationStatus();
        setVerificationStatus(response.data.verification_status);
      } catch (error) {
        console.error('Error checking verification status:', error);
        setVerificationStatus('unverified');
      } finally {
        setCheckingVerification(false);
      }
    };

    const fetchDocumentLimits = async () => {
      try {
        // Get user's organization ID from localStorage
        const organizationId = localStorage.getItem('organization_id');
        if (organizationId) {
          // Call API to get package limits
          const response = await api.getPackageLimits(organizationId);
          console.log('Package limits API response:', response);

          if (response.data && response.data.limits) {
            setPackageLimits(response.data.limits);

            // Extract document-related limits
            const limits = response.data.limits;

            const maxDocumentSizeBytes = limits.find(l => l.limit_type === 'max_document_size_bytes')?.limit_value;
            const maxTextLength = limits.find(l => l.limit_type === 'max_text_length')?.limit_value;
            const maxTokensPerDocument = limits.find(l => l.limit_type === 'max_tokens_per_document')?.limit_value;

            // Use API values if available, otherwise use reasonable defaults
            setDocumentLimits({
              maxDocumentSizeBytes: maxDocumentSizeBytes ? parseInt(maxDocumentSizeBytes) : 4194304, // 4MB default
              maxTextLength: maxTextLength ? parseInt(maxTextLength) : 4000, // 4K default
              maxTokensPerDocument: maxTokensPerDocument ? parseInt(maxTokensPerDocument) : 1000 // 1K default
            });
          } else {
            console.warn('No limits data in API response, using defaults');
            setDocumentLimits({
              maxDocumentSizeBytes: 4194304, // 4MB default
              maxTextLength: 4000,
              maxTokensPerDocument: 1000
            });
          }
        } else {
          console.warn('No organization ID found, using defaults');
          setDocumentLimits({
            maxDocumentSizeBytes: 4194304, // 4MB default
            maxTextLength: 4000,
            maxTokensPerDocument: 1000
          });
        }
      } catch (error) {
        console.error('Error fetching package limits:', error);
        // Use scale package defaults instead of spark defaults
        setDocumentLimits({
          maxDocumentSizeBytes: 4194304, // 4MB (scale package default)
          maxTextLength: 4000, // 4K (scale package default)
          maxTokensPerDocument: 1000 // 1K (scale package default)
        });
      } finally {
        setLoadingLimits(false);
      }
    };

    checkVerificationStatus();
    fetchDocumentLimits();
    fetchSystemSettings();
    fetchAvailableTools();
  }, []);

  // WebSocket connection setup and cleanup
  useEffect(() => {
    // Cleanup WebSocket on unmount
    return () => {
      if (window.websiteVerifyTimeout) {
        clearTimeout(window.websiteVerifyTimeout);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      // Dismiss initial toast when component unmounts
      toast.dismiss('agent-creation-progress');
      setSocketConnection(null);
    };
  }, []);

  // Setup WebSocket connection for task progress
  const setupWebSocketConnection = (taskId) => {
    if (!taskId || socketRef.current) return;

    // Dynamic WebSocket URL based on current domain
    const getWebSocketUrl = () => {
      if (process.env.REACT_APP_WEBSOCKET_URL) {
        return process.env.REACT_APP_WEBSOCKET_URL;
      }
      
      // Auto-detect based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      
      // For production (wsapp.snwolley.ai) use wss, for localhost use ws
      if (host.includes('localhost') || host.includes('127.0.0.1')) {
        return 'http://localhost:8081';
      } else {
        return `${protocol}//${host}`;
      }
    };

    const socketUrl = getWebSocketUrl();
    console.log('Setting up WebSocket connection to:', socketUrl);

    const socket = io(socketUrl, {
      transports: ['websocket'],
      timeout: 5000,
    });

    socket.on('connect', () => {
      console.log('WebSocket connected for task:', taskId);
      socket.emit('subscribe_task', { task_id: taskId });
      setSocketConnection(socket);
      
      // Simple connection confirmation
      toast.success('🔗 Connected to progress tracking', {
        duration: 2000
      });
      
      // Set a timeout to handle stuck tasks
      setTimeout(() => {
        if (loading && taskStatus !== 'completed' && taskStatus !== 'failed') {
          console.log('Task timeout - no progress received');
          setLoading(false);
          toast.dismiss('agent-creation-progress');
          toast.error('⏰ Task timed out - please try again', {
            duration: 5000
          });
          socket.disconnect();
          setSocketConnection(null);
          socketRef.current = null;
        }
      }, 120000); // 2 minutes timeout
    });

    socket.on('task_progress', (data) => {
      console.log('Task progress update:', data);

      // Handle individual validation errors as separate toasts
      if (data.step === 'validation_error') {
        // Mark that we've shown validation errors
        setHasShownValidationErrors(true);

        const errorType = data.details?.error_type;
        const filename = data.details?.filename || 'Unknown file';
        let toastMessage = data.message;
        let toastType = 'error';

        // Use the backend error message directly - it's already properly formatted
        toastMessage = `❌ ${data.message}`;

        // Show individual error toast (auto-generated safe ID)
        toast.error(toastMessage, {
          duration: 6000
        });
      } else {
        // Regular progress updates
        setTaskProgress(prev => [...prev, data.message]);
      }

      setTaskStatus(data.status);
      if (data.estimated_completion) {
        setEstimatedCompletion(data.estimated_completion);
      }
      
      // Show toast notification for each progress stage (DISABLED - keeping simple)
      // if (data.message && data.step) {
      //   const stageMessages = {
      //     'validating': '🔍 Validating agent configuration...',
      //     'creating_agent': '🤖 Creating AI agent...',
      //     'uploading_documents': '📄 Processing knowledge base...',
      //     'getting_container': '🚀 Setting up WhatsApp container...',
      //     'using_pool_container': '⚡ Using pre-warmed container...',
      //     'customizing_container': '⚙️ Customizing container settings...',
      //     'starting_container': '▶️ Starting WhatsApp service...',
      //     'creating_session': '🔗 Establishing WhatsApp connection...',
      //     'updating_database': '💾 Updating database...',
      //     'container_assigned': '✅ WhatsApp container ready...',
      //     'registering_client': '📝 Registering client...',
      //     'finalizing': '✨ Finalizing setup...'
      //   };
      //   
      //   const toastMessage = stageMessages[data.step] || data.message;
      //   
      //   // Update the persistent loading toast
      //   toast.loading(toastMessage, {
      //     id: 'agent-creation-progress'
      //   });
      //   
      //   // Show a brief flash toast for each step (so user sees all steps)
      //   toast(toastMessage, {
      //     duration: 1500,
      //     icon: '📋'
      //   });
      // }
    });

    socket.on('task_completed', async (data) => {
      console.log('Task completed:', data);
      setTaskProgress(prev => [...prev, 'Task completed successfully!']);
      setTaskStatus('completed');
      
      // Dismiss the initial loading toast
      toast.dismiss('agent-creation-progress');
      
      // Immediately disconnect socket to prevent any further events
      socket.disconnect();
      setSocketConnection(null);
      socketRef.current = null;
      
      // Upload documents if agent was created and documents exist
      if (data.result && data.result.agent_id && formData.knowledge_base_enabled && documents.length > 0) {
        try {
          toast.loading('📄 Uploading knowledge base documents...', {
            id: 'document-upload'
          });
          
          const uploadFormData = new FormData();
          documents.forEach((doc) => {
            if (doc.file && doc.file instanceof File) {
              uploadFormData.append('documents', doc.file);
            }
          });
          
          const validFileCount = documents.filter(doc => doc.file && doc.file instanceof File).length;
          if (validFileCount > 0) {
            await api.uploadAgentDocuments(data.result.agent_id, uploadFormData);
            toast.dismiss('document-upload');
            console.log('Documents uploaded successfully');
            setDocuments(prev => prev.map(doc => ({ ...doc, status: 'uploaded' })));
          }
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);
          toast.dismiss('document-upload');
          toast.error('⚠️ Agent created but document upload failed. You can upload them later from the agent settings.', {
            duration: 6000
          });
          setDocuments(prev => prev.map(doc => ({ ...doc, status: 'error' })));
        }
      }
      
      setLoading(false);
      
      if (data.result && data.result.client_id) {
        toast.success('🎉 Agent and WhatsApp connection created successfully!', {
          duration: 4000
        });
        navigate(`/dashboard/clients/${data.result.client_id}`);
      } else {
        toast.success('🎉 Agent created successfully!', {
          duration: 4000
        });
        navigate('/dashboard/clients');
      }
    });

    socket.on('task_failed', (data) => {
      console.log('Task failed:', data);
      setTaskProgress(prev => [...prev, `Task failed: ${data.error}`]);
      setTaskStatus('failed');
      setLoading(false);

      // Dismiss the initial loading toast
      toast.dismiss('agent-creation-progress');

      // Only show error toast if it's not a validation error (those are shown individually)
      const errorMessage = data.error || 'Agent creation failed';
      const isValidationError = errorMessage.includes('Document validation failed') ||
                               errorMessage.includes('validation failed') ||
                               hasShownValidationErrors;

      if (!isValidationError) {
        toast.error(`❌ ${errorMessage}`, {
          duration: 5000
        });
      }

      // Reset the validation error flag for next task
      setHasShownValidationErrors(false);

      // Disconnect socket
      socket.disconnect();
      setSocketConnection(null);
      socketRef.current = null;
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      setLoading(false); // Reset loading state
      toast.dismiss('agent-creation-progress');
      toast.error('❌ Failed to connect to task progress service', {
        duration: 4000
      });
      // Disconnect and clean up
      socket.disconnect();
      setSocketConnection(null);
      socketRef.current = null;
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setSocketConnection(null);
    });

    socketRef.current = socket;
  };

  // Create async task
  const createAsyncTask = async (taskData) => {
    try {
      console.log('Creating async task with data:', taskData);

      // Reset validation error flag for new task
      setHasShownValidationErrors(false);

      const authToken = localStorage.getItem('snwolley_auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          task_type: 'agent_creation',
          user_id: localStorage.getItem('user_id'),
          organization_id: localStorage.getItem('organization_id'),
          task_data: {
            ...taskData,
            auth_token: authToken
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.task_id) {
        console.log('Async task created successfully:', result.task_id);
        setAsyncTaskId(result.task_id);
        setTaskProgress(['Task created successfully, processing...']);
        setTaskStatus('processing');
        setupWebSocketConnection(result.task_id);
        return result.task_id;
      } else {
        throw new Error('Invalid response from task queue service');
      }
    } catch (error) {
      console.error('Error creating async task:', error);
      throw error;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked :
          type === 'number' ? parseFloat(value) || 0 : value
      };

      // Update instructions dynamically when agent name changes
      if (name === 'name' && !instructionsManuallyEdited) {
        const agentName = value.trim() || 'Snwolley';
        newData.instructions = `You are ${agentName}, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.`;
      }

      // Track if instructions were manually edited
      if (name === 'instructions') {
        const expectedInstruction = `You are ${prev.name.trim() || 'Snwolley'}, a helpful AI assistant. Respond to messages in a friendly and professional manner. Keep responses concise and relevant.`;
        if (value !== expectedInstruction) {
          setInstructionsManuallyEdited(true);
        }
      }

      return newData;
    });
  };

  // Document upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const validTypes = [
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'application/json'
      ];

      if (!validTypes.includes(file.type)) {
        toast.error(`File type ${file.type} is not supported. Please upload PDF, TXT, DOC, DOCX, CSV, or JSON files.`);
        return false;
      }

      const maxSizeMB = Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024));
      if (file.size > documentLimits.maxDocumentSizeBytes) {
        toast.error(`File ${file.name} is too large. Maximum size is ${maxSizeMB}MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      const newDocuments = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'ready'
      }));

      setDocuments(prev => [...prev, ...newDocuments]);

      // Clear the file input to allow re-uploading the same file
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const removeDocument = (documentId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const createWhatsAppClientWithTimeout = async (agentId, agentName, timeoutMs = 60000) => {
    return new Promise(async (resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('WhatsApp client creation timed out. Please try creating it manually from the agents dashboard.'));
      }, timeoutMs);

      try {
        const clientData = {
          display_name: `${agentName} Agent`,
          agent_id: agentId
        };

        const clientResponse = await api.createClient(clientData);
        clearTimeout(timeoutId);
        resolve(clientResponse);
      } catch (error) {
        clearTimeout(timeoutId);
        reject(error);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check verification status before proceeding
    if (verificationStatus !== 'verified') {
      setShowVerificationPrompt(true);
      return;
    }

    if (!formData.name.trim() || !formData.instructions.trim()) {
      toast.error('Agent name and instructions are required');
      return;
    }

    // Validate system prompt token limit (frontend check)
    const instructionsLength = formData.instructions.trim().length;
    const estimatedTokens = Math.ceil(instructionsLength / 4); // Rough estimation: 4 chars per token
    const maxTokens = maxSystemPromptTokens; // Use dynamic value from backend
    
    if (estimatedTokens > maxTokens) {
      toast.error(`System prompt too long (${estimatedTokens.toLocaleString()} tokens). Maximum allowed: ${maxTokens.toLocaleString()} tokens. Please reduce the length.`, {
        duration: 6000
      });
      return;
    }

    // Validate documents if knowledge base is enabled
    if (formData.knowledge_base_enabled && documents.length > 0) {
      const invalidDocuments = documents.filter(doc => !doc.file || doc.status === 'error');
      if (invalidDocuments.length > 0) {
        toast.error('Some documents are invalid. Please remove them and try again.');
        return;
      }
    }

    // Validate FAQ pairs if FAQ training is enabled
    if (faqTrainingEnabled) {
      const validFaqPairs = faqPairs.filter(pair => pair.question.trim() && pair.answer.trim());
      if (validFaqPairs.length === 0) {
        toast.error('Please add at least one complete FAQ pair or disable FAQ training.');
        return;
      }
    }

    try {
      setLoading(true);

      // Prepare agent data with FAQ if enabled
      const agentData = {
        ...formData,
        faq_enabled: faqTrainingEnabled,
        faq_pairs: faqTrainingEnabled ? faqPairs.filter(pair => pair.question.trim() && pair.answer.trim()) : [],
        website_training_enabled: websiteTrainingEnabled,
        website_url: websiteTrainingEnabled ? websiteUrl.trim() : '',
        selected_tools: selectedTools,
        documents: formData.knowledge_base_enabled ? await Promise.all(documents.map(async (doc) => {
          let content = null;
          if (doc.file && doc.file instanceof File) {
            // Convert file to base64 for transmission
            content = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(doc.file);
            });
          }
          return {
            name: doc.name,
            size: doc.size,
            type: doc.type,
            content: content
          };
        })) : []
      };

      // Choose async or sync mode based on environment variable
      if (isAsyncMode) {
        console.log('Using async mode for agent creation');
        toast.loading('🚀 Starting agent creation with progress tracking...', {
          id: 'agent-creation-progress',
          duration: Infinity // Keep until replaced or dismissed
        });
        
        try {
          // Create async task
          await createAsyncTask(agentData);
          // WebSocket will handle the rest of the process
          // Don't set loading to false here - let WebSocket events handle it
        } catch (asyncError) {
          console.error('Async task creation failed, falling back to sync mode:', asyncError);
          toast.warning('Falling back to standard creation process...');
          // Fall through to sync mode
          await handleSyncCreation(agentData);
        }
      } else {
        console.log('Using synchronous mode for agent creation');
        await handleSyncCreation(agentData);
      }

    } catch (error) {
      console.error('Error in agent creation process:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create agent';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Synchronous agent creation (original logic)
  const handleSyncCreation = async (agentData) => {
    // Step 1: Create the agent first
    console.log('Creating agent with data:', agentData);
    const response = await api.createAgent(agentData);

    if (!response.data || !response.data.agent || !response.data.agent.id) {
      throw new Error('Invalid response from server when creating agent');
    }

    const agentId = response.data.agent.id;
    const agentName = formData.name;

    // Step 2: Upload documents if knowledge base is enabled and documents are selected
    if (formData.knowledge_base_enabled && documents.length > 0) {
      console.log('Uploading documents...');
      setUploadingDocuments(true);

      // Update document status to uploading
      setDocuments(prev => prev.map(doc => ({ ...doc, status: 'uploading' })));

      try {
        // Create FormData for file upload
        const uploadFormData = new FormData();

        // Append each file with error handling
        documents.forEach((doc, index) => {
          if (doc.file && doc.file instanceof File) {
            uploadFormData.append('files', doc.file);
          } else {
            console.warn(`Document at index ${index} is not a valid File object:`, doc);
          }
        });

        // Only upload if we have valid files
        const validFileCount = documents.filter(doc => doc.file && doc.file instanceof File).length;
        if (validFileCount > 0) {
          await api.uploadAgentDocuments(agentId, uploadFormData);
          console.log('Documents uploaded successfully');

          // Update document status to uploaded
          setDocuments(prev => prev.map(doc => ({ ...doc, status: 'uploaded' })));
        } else {
          console.warn('No valid files to upload');
        }
      } catch (uploadError) {
        console.error('Error uploading documents:', uploadError);
        setDocuments(prev => prev.map(doc => ({ ...doc, status: 'error' })));
        toast.warning('Agent created but some documents failed to upload');
      } finally {
        setUploadingDocuments(false);
      }
    }

    // Step 3: Create WhatsApp client with extended timeout and better error handling
    console.log('Creating WhatsApp client...');
    setCreatingWhatsApp(true);

    try {
      const clientResponse = await createWhatsAppClientWithTimeout(agentId, agentName, 120000);

      // Check for successful response - handle different response structures
      const clientId = clientResponse.data?.client_id || clientResponse.data?.clientId || clientResponse.data?.id;
      
      if (!clientId) {
        throw new Error('Invalid response from server when creating WhatsApp client');
      }

      toast.success('Agent and WhatsApp connection created successfully!');
      navigate(`/dashboard/clients/${clientId}`);

    } catch (clientError) {
      console.error('Error creating WhatsApp client:', clientError);

      if (clientError.message.includes('timed out')) {
        toast.error('WhatsApp connection setup is taking longer than expected. Agent created successfully - you can set up WhatsApp manually.', {
          duration: 6000
        });
      } else if (clientError.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and try creating WhatsApp connection manually.');
      } else if (clientError.response?.status >= 500) {
        toast.error('Server error while creating WhatsApp connection. Agent created successfully - you can set it up manually.');
      } else {
        // Even if there's an error, if we have a client ID, the agent was created successfully
        const clientId = clientError.response?.data?.client_id || clientError.response?.data?.clientId || clientError.response?.data?.id;
        if (clientId) {
          toast.success('Agent and WhatsApp connection created successfully!.');
          navigate(`/dashboard/clients/${clientId}`);
          return; // Exit early since agent was created
        }
        toast.error('Agent created but failed to create WhatsApp connection. You can create it manually from the agents dashboard.');
      }

      setTimeout(() => {
        navigate('/dashboard/clients');
      }, 2000);
    } finally {
      setCreatingWhatsApp(false);
      setLoading(false);
    }
  };

  // Website verification function
  const verifyWebsite = async (url) => {
    setWebsiteVerificationStatus('verifying');
    setWebsiteVerificationError('');

    try {
      const response = await api.verifyWebsite(url);

      if (response.data.verified) {
        setWebsiteVerificationStatus('success');
        setFormData(prev => ({ ...prev, website_url: url }));
        toast.success('Website verified successfully!');
      } else {
        setWebsiteVerificationStatus('error');
        setWebsiteVerificationError(response.data.error || 'Website verification failed');
        toast.error(`Website verification failed: ${response.data.error}`);
      }
    } catch (error) {
      setWebsiteVerificationStatus('error');
      setWebsiteVerificationError('Failed to verify website');
      toast.error('Failed to verify website');
    }
  };

  // Handle website URL input change
  const handleWebsiteUrlChange = (e) => {
    const url = e.target.value;
    setWebsiteUrl(url);
    setWebsiteVerificationStatus(null);
    setWebsiteVerificationError('');

    // Auto-verify after user stops typing (debounce)
    if (url.trim()) {
      clearTimeout(window.websiteVerifyTimeout);
      window.websiteVerifyTimeout = setTimeout(() => {
        verifyWebsite(url.trim());
      }, 1000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/dashboard/agents')}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Agents
        </button>
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CpuChipIcon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create WhatsApp Agent</h1>
              <p className="text-sm text-gray-600 mt-1">Set up a new AI agent and connect to WhatsApp Business</p>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Account Verification Required</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  To create AI agents, you need to verify your account with valid identification documents.
                  This ensures security and compliance with platform requirements.
                </p>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start">
                    <DocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium">What you'll need:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>Valid Ghana ID (National ID, Driver's License, SSNIT, or Ghana Card)</li>
                        <li>Clear photos of your documents</li>
                        <li>Basic business information</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="items-center px-4 py-3">
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => navigate('/dashboard/verification')}
                    className="px-4 py-2 bg-whatsapp-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-whatsapp-600 focus:outline-none focus:ring-2 focus:ring-whatsapp-300"
                  >
                    Start Verification Process
                  </button>
                  <button
                    onClick={() => setShowVerificationPrompt(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status Banner */}
      {!checkingVerification && verificationStatus !== 'verified' && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Account Verification Pending</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your account needs to be verified before you can create AI agents.
                <button
                  onClick={() => navigate('/dashboard/verification')}
                  className="ml-1 font-medium underline hover:text-yellow-900"
                >
                  Complete verification now
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {checkingVerification && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <p className="text-sm text-blue-700">Checking account verification status...</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure the basic details of your AI agent
            </p>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Agent Name *
              </label>
              <input
                type="text"
                name="name"
                id="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                disabled={!checkingVerification && verificationStatus !== 'verified'}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="e.g., Customer Support Assistant"
              />
            </div>

            <div>
              <label htmlFor="instructions" className="block text-sm font-medium text-gray-700">
                System Instructions *
              </label>
              <div className="mt-1">
                <textarea
                  name="instructions"
                  id="instructions"
                  rows={6}
                  required
                  value={formData.instructions}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="Instructions will auto-update based on agent name..."
                />
              </div>
              {/* Token Counter */}
              <div className="mt-2 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  These instructions define how your AI agent will behave and respond to messages.
                </p>
                <div className="text-sm">
                  {(() => {
                    const charCount = formData.instructions.length;
                    const estimatedTokens = Math.ceil(charCount / 4);
                    const maxTokens = maxSystemPromptTokens;
                    const isNearLimit = estimatedTokens > maxTokens * 0.8;
                    const isOverLimit = estimatedTokens > maxTokens;
                    
                    return (
                      <span className={`font-medium ${
                        isOverLimit ? 'text-red-600' : 
                        isNearLimit ? 'text-amber-600' : 
                        'text-gray-500'
                      }`}>
                        ~{estimatedTokens.toLocaleString()} / {maxTokens.toLocaleString()} tokens
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Behavior Settings */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Behavior Settings</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure how the agent behaves in conversations
            </p>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="response_delay_ms" className="block text-sm font-medium text-gray-700">
                  Response Delay (ms)
                </label>
                <input
                  type="number"
                  name="response_delay_ms"
                  id="response_delay_ms"
                  min="0"
                  max="10000"
                  step="100"
                  value={formData.response_delay_ms}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Base delay before responding (plus response token calculation time)
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="auto_reply_enabled"
                  name="auto_reply_enabled"
                  type="checkbox"
                  checked={formData.auto_reply_enabled}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="auto_reply_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Enable auto-reply
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="typing_simulation"
                  name="typing_simulation"
                  type="checkbox"
                  checked={formData.typing_simulation}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="typing_simulation" className="ml-3 block text-sm font-medium text-gray-700">
                  Simulate typing indicator
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="knowledge_base_enabled"
                  name="knowledge_base_enabled"
                  type="checkbox"
                  checked={formData.knowledge_base_enabled}
                  onChange={handleInputChange}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="knowledge_base_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Enable knowledge base
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    Upload documents to train your agent with specific knowledge
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="website_training_enabled"
                  name="website_training_enabled"
                  type="checkbox"
                  checked={websiteTrainingEnabled}
                  onChange={(e) => setWebsiteTrainingEnabled(e.target.checked)}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="website_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Train with website
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    Train your agent with content from your website
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="faq_training_enabled"
                  name="faq_training_enabled"
                  type="checkbox"
                  checked={faqTrainingEnabled}
                  onChange={(e) => setFaqTrainingEnabled(e.target.checked)}
                  disabled={!checkingVerification && verificationStatus !== 'verified'}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="faq_training_enabled" className="ml-3 block text-sm font-medium text-gray-700">
                  Train with FAQ
                </label>
                <div className="ml-2 group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Add custom question-answer pairs to train your agent
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge Base Documents */}
        {formData.knowledge_base_enabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Knowledge Base Documents</h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload documents to train your agent with specific knowledge (PDF, TXT, DOC, DOCX, CSV, JSON)
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* File Upload Dropzone */}
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Drop files here or click to upload
                      </span>
                      <span className="mt-1 block text-xs text-gray-500">
                        PDF, TXT, DOC, DOCX, CSV, JSON up to {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB each
                      </span>
                    </label>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      accept=".pdf,.txt,.doc,.docx,.csv,.json"
                      className="sr-only"
                      onChange={handleFileInput}
                      disabled={!checkingVerification && verificationStatus !== 'verified'}
                    />
                  </div>
                </div>
              </div>

              {/* Uploaded Documents List */}
              {documents.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Uploaded Documents ({documents.length})</h4>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(doc.size)} • {doc.type.split('/')[1].toUpperCase()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            doc.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                              doc.status === 'uploading' ? 'bg-yellow-100 text-yellow-800' :
                                doc.status === 'uploaded' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                          }`}>
                            {doc.status === 'ready' ? 'Ready' :
                              doc.status === 'uploading' ? 'Uploading...' :
                                doc.status === 'uploaded' ? 'Uploaded' :
                                  'Error'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeDocument(doc.id)}
                            className="text-gray-400 hover:text-red-500"
                            disabled={doc.status === 'uploading'}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Base Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">Knowledge Base Training</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Documents will be processed and used to train your agent. The agent will be able to answer questions based on the content of these documents.
                    </p>
                    <div className="mt-2">
                      <h5 className="text-xs font-medium text-blue-800">Supported Formats:</h5>
                      <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                        <li>PDF files (.pdf)</li>
                        <li>Word documents (.doc, .docx)</li>
                        <li>Plain text (.txt)</li>
                        <li>CSV files (.csv)</li>
                        <li>JSON files (.json)</li>
                      </ul>
                      <p className="text-xs text-blue-600 mt-1">
                        Maximum: {Math.floor(documentLimits.maxDocumentSizeBytes / (1024 * 1024))}MB per file, {documentLimits.maxTextLength.toLocaleString()} characters, {documentLimits.maxTokensPerDocument.toLocaleString()} tokens
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Custom Tools Selection */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Custom Tools</h3>
            <p className="text-sm text-gray-500 mt-1">
              Select custom HTTP tools that your agent can use to interact with external APIs
            </p>
          </div>
          <div className="px-6 py-4">
            {loadingTools ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading available tools...</p>
              </div>
            ) : availableTools.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-gray-400 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.78 0-2.674-2.155-1.414-3.414l5-5A2 2 0 009 9.172V5L8 4z" />
                  </svg>
                </div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">No tools available</h4>
                <p className="text-sm text-gray-600 mb-4">
                  You haven't created any custom tools yet.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/agents/tools')}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Create Tools
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableTools.map((tool) => (
                    <div
                      key={tool.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedTools.includes(tool.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        if (selectedTools.includes(tool.id)) {
                          setSelectedTools(prev => prev.filter(id => id !== tool.id));
                        } else {
                          setSelectedTools(prev => [...prev, tool.id]);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            tool.http_method === 'GET' ? 'bg-green-100 text-green-800' :
                            tool.http_method === 'POST' ? 'bg-blue-100 text-blue-800' :
                            tool.http_method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                            tool.http_method === 'DELETE' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tool.http_method}
                          </span>
                          <input
                            type="checkbox"
                            checked={selectedTools.includes(tool.id)}
                            onChange={() => {}}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">{tool.name}</h4>
                      {tool.description && (
                        <p className="text-xs text-gray-600 mb-2">{tool.description}</p>
                      )}
                      <p className="text-xs text-gray-500 truncate">{tool.url}</p>
                      {tool.parameters && tool.parameters.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {tool.parameters.length} parameter{tool.parameters.length !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {selectedTools.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-blue-800">
                          {selectedTools.length} tool{selectedTools.length !== 1 ? 's' : ''} selected
                        </h4>
                        <p className="mt-1 text-sm text-blue-700">
                          Your agent will be able to call these tools during conversations to provide enhanced functionality.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Website Training */}
        {websiteTrainingEnabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Website Training</h3>
              <p className="text-sm text-gray-500 mt-1">
                Enter a website URL to train your agent with content from that website
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Website URL Input */}
              <div>
                <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={websiteUrl}
                    onChange={handleWebsiteUrlChange}
                    placeholder="https://example.com"
                    disabled={!checkingVerification && verificationStatus !== 'verified'}
                    className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  />
                  {/* Verification Status Icons */}
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {websiteVerificationStatus === 'verifying' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    )}
                    {websiteVerificationStatus === 'success' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    )}
                    {websiteVerificationStatus === 'error' && (
                      <XCircleIcon className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
                {/* Error Message */}
                {websiteVerificationStatus === 'error' && websiteVerificationError && (
                  <p className="mt-1 text-sm text-red-600">{websiteVerificationError}</p>
                )}
                {/* Success Message */}
                {websiteVerificationStatus === 'success' && (
                  <p className="mt-1 text-sm text-green-600">Website verified successfully!</p>
                )}
              </div>

              {/* Website Training Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-green-800">Website Content Training</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Your agent will be trained with the content from the provided website. This allows the agent to answer questions based on the website's information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Training */}
        {faqTrainingEnabled && (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">FAQ Training</h3>
              <p className="text-sm text-gray-500 mt-1">
                Add question-answer pairs to train your agent with specific responses
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* FAQ Pairs */}
              <div className="space-y-4">
                {faqPairs.map((pair, index) => (
                  <div key={pair.id} className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium text-gray-700">FAQ Pair {index + 1}</h4>
                      {faqPairs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFaqPair(pair.id)}
                          className="text-red-500 hover:text-red-700"
                          disabled={!checkingVerification && verificationStatus !== 'verified'}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Question
                      </label>
                      <textarea
                        rows={2}
                        value={pair.question}
                        onChange={(e) => updateFaqPair(pair.id, 'question', e.target.value)}
                        placeholder="Enter a question that users might ask..."
                        disabled={!checkingVerification && verificationStatus !== 'verified'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Answer
                      </label>
                      <textarea
                        rows={3}
                        value={pair.answer}
                        onChange={(e) => updateFaqPair(pair.id, 'answer', e.target.value)}
                        placeholder="Enter the response your agent should give..."
                        disabled={!checkingVerification && verificationStatus !== 'verified'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Add FAQ Pair Button */}
              <button
                type="button"
                onClick={addFaqPair}
                disabled={!checkingVerification && verificationStatus !== 'verified'}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-gray-600">
                  <DocumentTextIcon className="mx-auto h-8 w-8 mb-2" />
                  <span className="block text-sm font-medium">Add Another FAQ Pair</span>
                  <span className="block text-xs text-gray-500 mt-1">Click to add more questions and answers</span>
                </div>
              </button>

              {/* FAQ Training Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">FAQ Training</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Your agent will be trained with these question-answer pairs. When users ask similar questions, the agent will provide responses based on this training data.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard/agents')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploadingDocuments || creatingWhatsApp || (!checkingVerification && verificationStatus !== 'verified')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {uploadingDocuments ? 'Uploading Documents...' :
                  creatingWhatsApp ? 'Creating WhatsApp Connection...' :
                    'Creating Agent...'}
              </>
            ) : (
              <>
                <CpuChipIcon className="h-4 w-4 mr-2" />
                🚀 Create WhatsApp Agent
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAgent;